import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, ImagePlus, X, Download, Trash2, Loader2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { apiError } from "@/lib/api";
import { downloadBlob } from "@/lib/exporters";
import {
  MAX_PHOTOS_PER_VISIT, listVisitImages, deleteVisitImage, fetchImageBlob, uploadVisitImages,
} from "@/lib/visitImages";

const PICKER_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/*";
const dateLabel = (d) => new Date(`${(d || "").slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// Patient photos sit behind login, so they're fetched with the auth header and shown from a blob
// (the session token never appears in an image URL, where it would end up in logs).
function AuthImage({ path, alt, className, onLoaded }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = "";
    let live = true;
    setSrc(""); setFailed(false);
    fetchImageBlob(path).then((blob) => {
      if (!live) return;
      url = URL.createObjectURL(blob);
      setSrc(url);
      onLoaded?.(blob);
    }).catch(() => live && setFailed(true));
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  if (failed) return <div className={`grid place-items-center bg-slate-100 text-xs text-slate-400 ${className}`}>Can't load</div>;
  if (!src) return <div className={`animate-pulse bg-slate-100 ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

// Choose photos while logging a visit. They upload right after the visit is saved.
export function PhotoPicker({ files, setFiles }) {
  const { online } = useApp();
  const input = useRef(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const add = (e) => {
    const all = [...e.target.files];
    e.target.value = "";
    const picked = all.filter((f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(f.name));
    if (picked.length < all.length) toast.error("Only photos can be attached");
    const room = MAX_PHOTOS_PER_VISIT - files.length;
    if (picked.length > room) toast.error(`Up to ${MAX_PHOTOS_PER_VISIT} photos per visit`);
    setFiles([...files, ...picked.slice(0, Math.max(room, 0))]);
  };

  return (
    <section className="dm-panel p-4 md:p-5" data-testid="av-photos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-1.5"><Camera size={13} /> Photos &amp; X-rays <span className="normal-case tracking-normal text-slate-400">(optional)</span></p>
        <button type="button" data-testid="av-photos-add" onClick={() => input.current?.click()}
          disabled={files.length >= MAX_PHOTOS_PER_VISIT || !online} className="dm-btn-ghost !py-1.5 text-sm disabled:opacity-40">
          <ImagePlus size={15} /> Add photos
        </button>
        <input ref={input} type="file" multiple accept={PICKER_ACCEPT} className="hidden" data-testid="av-photos-input" onChange={add} />
      </div>
      {!online && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-700"><WifiOff size={13} /> Photos need an internet connection. You can add them from the patient's page later.</p>
      )}
      {files.length > 0 && (
        <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6" data-testid="av-photos-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-slate-900/70 text-white hover:bg-slate-900"><X size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Every photo for a patient, grouped by visit, with a viewer.
export function PatientPhotos({ patientId, visits }) {
  const { isOwner, online } = useApp();
  const [images, setImages] = useState(null);
  const [view, setView] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [target, setTarget] = useState("");
  const [uploading, setUploading] = useState(false);
  const input = useRef(null);
  const blobs = useRef({});

  const load = useCallback(async () => {
    try { setImages(await listVisitImages(patientId)); } catch { setImages((cur) => cur || []); }
  }, [patientId]);
  useEffect(() => { load(); }, [load, visits.length]);

  const visitById = useMemo(() => Object.fromEntries(visits.map((v) => [v.id, v])), [visits]);
  const groups = useMemo(() => {
    const by = {};
    for (const im of images || []) if (visitById[im.visit_id]) (by[im.visit_id] ||= []).push(im);
    return visits.filter((v) => by[v.id]).map((v) => ({ visit: v, images: by[v.id] }));
  }, [images, visits, visitById]);

  const visitId = target && visitById[target] ? target : visits[0]?.id || "";
  const used = (images || []).filter((i) => i.visit_id === visitId).length;

  const onPick = async (e) => {
    const picked = [...e.target.files];
    e.target.value = "";
    if (!picked.length || !visitId) return;
    if (used + picked.length > MAX_PHOTOS_PER_VISIT) return toast.error(`A visit can have up to ${MAX_PHOTOS_PER_VISIT} photos`);
    setUploading(true);
    const { uploaded, failed } = await uploadVisitImages(visitId, picked);
    setUploading(false);
    await load();
    if (uploaded) toast.success(`${uploaded} photo${uploaded === 1 ? "" : "s"} added`);
    if (failed.length) toast.error(`${failed.length} couldn't upload: ${failed[0].error}`);
  };

  const remove = async () => {
    try {
      await deleteVisitImage(view.id);
      toast.success("Photo deleted");
      setView(null); setConfirming(false);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const download = () => {
    const blob = blobs.current[view.id];
    if (blob) downloadBlob(blob, view.original_filename || "photo.jpg");
  };

  if (!visits.length) return null;
  const viewVisit = view && visitById[view.visit_id];

  return (
    <div className="dm-panel overflow-hidden" data-testid="patient-photos">
      <div className="dm-panel-head">
        <h2 className="flex items-center gap-2 font-head text-lg font-semibold text-slate-800"><Camera size={17} /> Photos &amp; X-rays
          {images?.length > 0 && <span className="font-mono text-xs font-normal text-slate-500">{images.length}</span>}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="photos-visit" aria-label="Attach to visit" className="dm-input !w-auto !py-1.5 text-sm" value={visitId} onChange={(e) => setTarget(e.target.value)}>
            {visits.map((v) => <option key={v.id} value={v.id}>{dateLabel(v.date)}{v.treatment ? ` · ${v.treatment}` : ""}</option>)}
          </select>
          <button type="button" data-testid="photos-add" onClick={() => input.current?.click()}
            disabled={uploading || !online || used >= MAX_PHOTOS_PER_VISIT} className="dm-btn-ghost !py-1.5 text-sm disabled:opacity-40">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Add photos
          </button>
          <input ref={input} type="file" multiple accept={PICKER_ACCEPT} className="hidden" data-testid="photos-input" onChange={onPick} />
        </div>
      </div>

      {images === null ? (
        <div className="dm-empty m-4">Loading photos…</div>
      ) : groups.length === 0 ? (
        <div className="dm-empty m-4" data-testid="photos-empty">{online ? "No photos yet. Add X-rays or clinical photos to a visit." : "No photos yet."}</div>
      ) : (
        <div className="space-y-5 p-5">
          {groups.map(({ visit, images: list }) => (
            <div key={visit.id}>
              <p className="mb-2 text-xs font-semibold text-slate-600">
                {dateLabel(visit.date)}{visit.treatment ? ` · ${visit.treatment}` : ""}{visit.diagnosis ? ` · ${visit.diagnosis}` : ""}
              </p>
              <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                {list.map((im) => (
                  <li key={im.id}>
                    <button type="button" data-testid={`photo-${im.id}`} onClick={() => { setConfirming(false); setView(im); }}
                      className="block aspect-square w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2"
                      style={{ "--tw-ring-color": "var(--dm-accent)" }}>
                      <AuthImage path={im.storage_path} alt={`Photo from ${dateLabel(visit.date)}`} className="h-full w-full object-cover" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(open) => { if (!open) { setView(null); setConfirming(false); } }}>
        <DialogContent className="max-w-3xl gap-3 rounded-2xl border-slate-200 bg-white p-4 text-slate-900 sm:rounded-2xl" data-testid="photo-viewer">
          <DialogHeader>
            <DialogTitle className="font-head text-base font-semibold text-slate-900">
              {viewVisit ? `${dateLabel(viewVisit.date)}${viewVisit.treatment ? ` · ${viewVisit.treatment}` : ""}` : "Photo"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{view?.original_filename}</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="grid max-h-[70vh] place-items-center overflow-auto rounded-xl bg-slate-900">
              <AuthImage key={view.id} path={view.storage_path} alt={view.original_filename || "Photo"} className="max-h-[70vh] w-auto max-w-full object-contain"
                onLoaded={(blob) => { blobs.current[view.id] = blob; }} />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button type="button" data-testid="photo-download" onClick={download} className="dm-btn-ghost !py-1.5 text-sm"><Download size={15} /> Download</button>
            {isOwner && (confirming ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">Delete this photo?</span>
                <button type="button" onClick={() => setConfirming(false)} className="dm-btn-quiet !py-1.5 text-sm">Keep</button>
                <button type="button" data-testid="photo-delete-confirm" onClick={remove} className="dm-btn-danger !py-1.5 text-sm"><Trash2 size={15} /> Delete</button>
              </div>
            ) : (
              <button type="button" data-testid="photo-delete" onClick={() => setConfirming(true)} className="dm-btn-danger !py-1.5 text-sm"><Trash2 size={15} /> Delete</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
